/**
 * Skeleton Component - Base skeleton component with variants and shimmer effect
 * 
 * Part of unified skeleton system for consistent loading states.
 * Uses Nightfall design tokens for theming.
 */

// Simple className utility for skeleton components
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  variant?: 'default' | 'circular' | 'text' | 'image';
  width?: string | number;
  height?: string | number;
  showShimmer?: boolean;
}

export function Skeleton({
  className,
  variant = 'default',
  width,
  height,
  showShimmer = true,
  style,
  ...props
}: SkeletonProps) {
  const baseClasses = 'bg-oro-surface-alt animate-pulse';
  
  const variantClasses = {
    default: 'rounded-md',
    circular: 'rounded-full',
    text: 'rounded-sm',
    image: 'rounded-lg'
  };

  const shimmerClasses = showShimmer 
    ? 'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/5 before:to-transparent' 
    : '';

  const combinedStyle = {
    width,
    height,
    ...style
  };

  return (
    <>
      {/* Add shimmer keyframes to global styles if not already present */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes shimmer {
            100% {
              transform: translateX(100%);
            }
          }
        `
      }} />
      
      <div
        className={cn(
          baseClasses,
          variantClasses[variant],
          shimmerClasses,
          className
        )}
        style={combinedStyle}
        {...props}
      />
    </>
  );
}